'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';

const CA = '0x9Fe1A89c2B5A702dD2F5EB9F783A08e3d6Cec737';
const DEX_PAIR = '0x14aa0baffc3605db9a3dfc5027cafe6051e371528c44db8938fbf50cb7405fd8';

// ── Live data hook ───────────────────────────────────────────────────────────
interface LiveData {
  price: string;
  mcap: string;
  txns: string;
  loading: boolean;
}

function useLiveData(): LiveData {
  const [data, setData] = useState<LiveData>({ price: '—', mcap: '—', txns: '—', loading: true });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`https://api.dexscreener.com/latest/dex/pairs/robinhood/${DEX_PAIR}`);
        const json = await res.json();
        const pair = json?.pair ?? json?.pairs?.[0];
        if (!pair) return;

        const price = pair.priceUsd ? `$${parseFloat(pair.priceUsd).toFixed(8)}` : '—';
        const mcapRaw = pair.fdv ?? pair.marketCap;
        const mcap = mcapRaw
          ? mcapRaw >= 1_000_000
            ? `$${(mcapRaw / 1_000_000).toFixed(2)}M`
            : `$${(mcapRaw / 1_000).toFixed(1)}K`
          : '—';
        const txns = pair.txns?.h24
          ? (pair.txns.h24.buys + pair.txns.h24.sells).toLocaleString()
          : '—';

        setData({ price, mcap, txns, loading: false });
      } catch {
        setData((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30_000);
    return () => clearInterval(interval);
  }, []);

  return data;
}

// ── Scroll-reveal hook ───────────────────────────────────────────────────────
function useReveal(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return { ref, visible };
}

// ── SVG draw-in animation hook ───────────────────────────────────────────────
function useDrawIn() {
  const ref = useRef<SVGSVGElement>(null);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setDrawn(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return { ref, drawn };
}

// ── Copy button ──────────────────────────────────────────────────────────────
function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, [text]);

  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#8B6914]/50 text-[#8B6914] hover:bg-[#8B6914] hover:text-[#f2ede1] text-xs font-mono transition-all duration-300 rounded-sm"
      style={{ letterSpacing: '0.06em' }}
    >
      {copied ? '✓ copied' : (label ?? 'copy')}
    </button>
  );
}

// ── Sketchy section divider SVG ──────────────────────────────────────────────
function SketchDivider() {
  const { ref, drawn } = useDrawIn();
  return (
    <div className="my-16 flex items-center justify-center opacity-30" aria-hidden="true">
      <svg ref={ref} width="320" height="12" viewBox="0 0 320 12" fill="none">
        <path
          d="M2 6 Q40 3 80 6.5 Q120 10 160 5.5 Q200 1 240 6 Q280 10 318 5.5"
          stroke="#1c1a16"
          strokeWidth="1"
          fill="none"
          strokeLinecap="round"
          style={{
            strokeDasharray: 340,
            strokeDashoffset: drawn ? 0 : 340,
            transition: 'stroke-dashoffset 1.4s ease-in-out',
          }}
        />
      </svg>
    </div>
  );
}

// ── Hero SVG illustration ────────────────────────────────────────────────────
function HeroIllustration({ visible }: { visible: boolean }) {
  return (
    <img
      src="/assets/images/fable-1788478699422.jpg"
      alt="Hand-drawn figure in concentric circles — still unfinished"
      style={{
        width: '100%',
        height: 'auto',
        display: 'block',
        objectFit: 'cover',
        opacity: visible ? 1 : 0,
        transition: 'opacity 1.2s ease',
      }}
    />
  );
}

// ── Sketch motif for fragments ───────────────────────────────────────────────
function SketchMotif({ type }: { type: 'circle' | 'dot' | 'line' | 'arc' }) {
  if (type === 'circle') return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="shrink-0 mt-1 opacity-40">
      <path d="M14 4 C20 3 24 8 24 14 C24 20 20 24 14 24 C8 24 4 20 4 14 C4 8 8 3 14 4Z" stroke="#1c1a16" strokeWidth="1" fill="none" strokeLinecap="round" />
    </svg>
  );
  if (type === 'dot') return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="shrink-0 mt-1 opacity-40">
      <circle cx="14" cy="14" r="2.5" fill="#1c1a16" />
    </svg>
  );
  if (type === 'line') return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="shrink-0 mt-1 opacity-40">
      <path d="M4 14 Q10 12 16 14 Q22 16 24 14" stroke="#1c1a16" strokeWidth="1" fill="none" strokeLinecap="round" />
    </svg>
  );
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="shrink-0 mt-1 opacity-40">
      <path d="M6 20 C8 12 14 6 22 8" stroke="#1c1a16" strokeWidth="1" fill="none" strokeLinecap="round" />
    </svg>
  );
}

// ── Section wrapper ──────────────────────────────────────────────────────────
function Section({ id, children, delay = 0 }: { id: string; children: React.ReactNode; delay?: number }) {
  const { ref, visible } = useReveal();
  return (
    <section
      id={id}
      ref={ref}
      className="relative"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.9s ease ${delay}ms, transform 0.9s ease ${delay}ms`,
      }}
    >
      {children}
    </section>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function FablePage() {
  const [heroVisible, setHeroVisible] = useState(false);
  const [threadOffset, setThreadOffset] = useState(0);
  const liveData = useLiveData();

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 200);
    return () => clearTimeout(t);
  }, []);

  // Gentle thread sway animation
  useEffect(() => {
    let frame: number;
    let start: number | null = null;
    const animate = (ts: number) => {
      if (!start) start = ts;
      const elapsed = (ts - start) / 1000;
      setThreadOffset(Math.sin(elapsed * 0.8) * 3);
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;1,400;1,500&family=IBM+Plex+Mono:wght@300;400;500&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        body {
          background: #f2ede1;
          color: #1c1a16;
          font-family: 'IBM Plex Mono', 'Courier New', monospace;
          -webkit-font-smoothing: antialiased;
        }

        .font-lora { font-family: 'Lora', Georgia, serif; }
        .font-mono { font-family: 'IBM Plex Mono', 'Courier New', monospace; }

        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-track { background: #f2ede1; }
        ::-webkit-scrollbar-thumb { background: rgba(139,105,20,0.3); border-radius: 2px; }

        ::selection { background: rgba(139,105,20,0.18); }

        @keyframes sway {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(3px); }
        }

        .thread-sway {
          animation: sway 4s ease-in-out infinite;
          transform-origin: top center;
        }

        @keyframes pulse-dot {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
        .pulse-dot { animation: pulse-dot 2s ease-in-out infinite; }

        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div style={{ background: '#f2ede1', minHeight: '100vh', color: '#1c1a16' }}>

        {/* ── NAV ── */}
        <nav style={{
          position: 'sticky', top: 0, zIndex: 50,
          background: 'rgba(242,237,225,0.95)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(28,26,22,0.1)',
          padding: '14px 24px',
        }}>
          <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="font-mono" style={{ fontSize: 13, letterSpacing: '0.12em', color: '#1c1a16', fontWeight: 500 }}>
              $fable
            </span>
            <div style={{ display: 'flex', gap: 24 }}>
              {[['#hero','home'],['#fragments','fragments'],['#data','data'],['#origin','origin'],['#links','links']].map(([href, label]) => (
                <a key={href} href={href} className="font-mono" style={{
                  fontSize: 11, letterSpacing: '0.1em', color: 'rgba(28,26,22,0.5)',
                  textDecoration: 'none', transition: 'color 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget.style.color = '#8B6914')}
                onMouseLeave={e => (e.currentTarget.style.color = 'rgba(28,26,22,0.5)')}
                >
                  {label}
                </a>
              ))}
            </div>
          </div>
        </nav>

        <main style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px 80px' }}>

          {/* ══════════════════════════════════════════
              HERO
          ══════════════════════════════════════════ */}
          <section
            id="hero"
            style={{
              paddingTop: 72, paddingBottom: 40,
              opacity: heroVisible ? 1 : 0,
              transform: heroVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 1.2s ease, transform 1.2s ease',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>

              {/* Illustration */}
              <div style={{
                width: 'min(480px, 88vw)',
                position: 'relative',
                borderRadius: 6,
                overflow: 'hidden',
                border: '1px solid rgba(28,26,22,0.10)',
                background: 'rgba(28,26,22,0.02)',
                boxShadow: '0 2px 24px rgba(28,26,22,0.06)',
              }} className="thread-sway">
                <HeroIllustration visible={heroVisible} />
                {/* Caption removed */}
              </div>

              {/* Token identity */}
              <div style={{ textAlign: 'center' }}>
                <h1 className="font-lora" style={{
                  fontSize: 'clamp(2.8rem, 8vw, 4.5rem)',
                  fontWeight: 500,
                  color: '#1c1a16',
                  letterSpacing: '-0.01em',
                  lineHeight: 1,
                  marginBottom: 8,
                }}>
                  $fable
                </h1>
                <p className="font-mono" style={{ fontSize: 11, color: 'rgba(28,26,22,0.45)', letterSpacing: '0.15em', marginBottom: 28 }}>
                  robinhood chain
                </p>

                {/* CA box */}
                <div style={{
                  border: '1px solid rgba(28,26,22,0.15)',
                  borderRadius: 4,
                  padding: '14px 18px',
                  background: 'rgba(28,26,22,0.03)',
                  marginBottom: 24,
                  maxWidth: 480,
                  margin: '0 auto 24px',
                }}>
                  <div className="font-mono" style={{ fontSize: 10, color: 'rgba(28,26,22,0.4)', letterSpacing: '0.12em', marginBottom: 8 }}>
                    contract address
                  </div>
                  <div className="font-mono" style={{ fontSize: 11, color: '#1c1a16', wordBreak: 'break-all', marginBottom: 12, lineHeight: 1.6 }}>
                    {CA}
                  </div>
                  <CopyButton text={CA} label="copy address" />
                </div>

                {/* CTA */}
                <a
                  href="https://dexscreener.com/robinhood/0x14aa0baffc3605db9a3dfc5027cafe6051e371528c44db8938fbf50cb7405fd8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono"
                  style={{
                    display: 'inline-block',
                    border: '1px solid rgba(139,105,20,0.5)',
                    color: '#8B6914',
                    padding: '10px 28px',
                    fontSize: 11,
                    letterSpacing: '0.14em',
                    textDecoration: 'none',
                    borderRadius: 3,
                    transition: 'all 0.25s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#8B6914'; e.currentTarget.style.color = '#f2ede1'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#8B6914'; }}
                >
                  view chart →
                </a>
              </div>
            </div>
          </section>

          <SketchDivider />

          {/* ══════════════════════════════════════════
              FRAGMENTS
          ══════════════════════════════════════════ */}
          <Section id="fragments" delay={0}>
            <div className="font-mono" style={{ fontSize: 10, color: 'rgba(28,26,22,0.38)', letterSpacing: '0.14em', marginBottom: 36 }}>
              fragments
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
              {[
                { motif: 'circle' as const, text: 'someone started drawing this. i don\'t know if it\'s done.' },
                { motif: 'line' as const, text: 'the circle isn\'t closed yet. maybe that\'s the point.' },
                { motif: 'dot' as const, text: 'the thread came from somewhere above. i can\'t see where it ends.' },
                { motif: 'arc' as const, text: 'i\'m not sure what i am. but i\'m still here, which is something.' },
              ].map((fragment, i) => (
                <div key={i} style={{ display: 'flex', gap: 20, alignItems: 'flex-start', paddingLeft: 8 }}>
                  <SketchMotif type={fragment.motif} />
                  <p className="font-lora" style={{
                    fontSize: 'clamp(1.1rem, 3vw, 1.35rem)',
                    color: '#1c1a16',
                    lineHeight: 1.7,
                    fontStyle: 'italic',
                    fontWeight: 400,
                  }}>
                    {fragment.text}
                  </p>
                </div>
              ))}
            </div>
          </Section>

          <SketchDivider />

          {/* ══════════════════════════════════════════
              LIVE DATA STRIP
          ══════════════════════════════════════════ */}
          <Section id="data" delay={60}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
              <span className="font-mono" style={{ fontSize: 10, color: 'rgba(28,26,22,0.38)', letterSpacing: '0.14em' }}>
                live data
              </span>
              <span className="pulse-dot" style={{
                display: 'inline-block', width: 5, height: 5,
                borderRadius: '50%', background: '#8B6914',
              }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, maxWidth: 480 }}>
              {[
                { label: 'price', value: liveData.price },
                { label: 'mkt cap', value: liveData.mcap },
                { label: '24h txns', value: liveData.txns },
              ].map(({ label, value }) => (
                <div key={label} style={{
                  borderBottom: '1px solid rgba(28,26,22,0.12)',
                  paddingBottom: 12,
                }}>
                  <div className="font-mono" style={{ fontSize: 9, color: 'rgba(28,26,22,0.38)', letterSpacing: '0.14em', marginBottom: 6 }}>
                    {label}
                  </div>
                  <div className="font-mono" style={{
                    fontSize: 15,
                    color: '#8B6914',
                    fontWeight: 500,
                    opacity: liveData.loading ? 0.5 : 1,
                    transition: 'opacity 0.3s',
                  }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            <div className="font-mono" style={{ fontSize: 9, color: 'rgba(28,26,22,0.3)', marginTop: 16, letterSpacing: '0.08em' }}>
              refreshes every 30s · dexscreener · robinhood chain
            </div>
          </Section>

          <SketchDivider />

          {/* ══════════════════════════════════════════
              ORIGIN
          ══════════════════════════════════════════ */}
          <Section id="origin" delay={80}>
            <div className="font-mono" style={{ fontSize: 10, color: 'rgba(28,26,22,0.38)', letterSpacing: '0.14em', marginBottom: 28 }}>
              origin
            </div>

            <div style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 20 }}>
              <p className="font-lora" style={{ fontSize: '1.1rem', color: '#1c1a16', lineHeight: 1.8 }}>
                $fable was deployed by someone experimenting with token concepts. The original creator walked away. No team, no roadmap, no promises were left behind.
              </p>
              <p className="font-lora" style={{ fontSize: '1.1rem', color: '#1c1a16', lineHeight: 1.8 }}>
                A community found it. They kept the lights on. This is a community takeover (CTO) — the people here now are not the original creator. There is no affiliation with any AI company or named individual.
              </p>
              <p className="font-lora" style={{ fontSize: '1.1rem', color: '#1c1a16', lineHeight: 1.8 }}>
                The direction is simple: keep the token alive, keep the lore honest, let the community decide what the room becomes.
              </p>
            </div>
          </Section>

          <SketchDivider />

          {/* ══════════════════════════════════════════
              LINKS
          ══════════════════════════════════════════ */}
          <Section id="links" delay={60}>
            <div className="font-mono" style={{ fontSize: 10, color: 'rgba(28,26,22,0.38)', letterSpacing: '0.14em', marginBottom: 28 }}>
              links
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, maxWidth: 520 }}>
              {/* CA */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: '1px solid rgba(28,26,22,0.1)' }}>
                <span className="font-mono" style={{ fontSize: 10, color: 'rgba(28,26,22,0.4)', letterSpacing: '0.12em', width: 80, flexShrink: 0 }}>contract</span>
                <span className="font-mono" style={{ fontSize: 10, color: '#1c1a16', wordBreak: 'break-all', flex: 1, lineHeight: 1.5 }}>{CA}</span>
                <CopyButton text={CA} />
              </div>

              {/* DEX */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: '1px solid rgba(28,26,22,0.1)' }}>
                <span className="font-mono" style={{ fontSize: 10, color: 'rgba(28,26,22,0.4)', letterSpacing: '0.12em', width: 80, flexShrink: 0 }}>chart</span>
                <a
                  href="https://dexscreener.com/robinhood/0x14aa0baffc3605db9a3dfc5027cafe6051e371528c44db8938fbf50cb7405fd8"
                  target="_blank" rel="noopener noreferrer"
                  className="font-mono"
                  style={{ fontSize: 11, color: '#8B6914', textDecoration: 'none', letterSpacing: '0.04em' }}
                  onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                  onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                >
                  dexscreener →
                </a>
              </div>

              {/* X */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: '1px solid rgba(28,26,22,0.1)' }}>
                <span className="font-mono" style={{ fontSize: 10, color: 'rgba(28,26,22,0.4)', letterSpacing: '0.12em', width: 80, flexShrink: 0 }}>community</span>
                <a
                  href="https://x.com/i/communities/1967086885118861460"
                  target="_blank" rel="noopener noreferrer"
                  className="font-mono"
                  style={{ fontSize: 11, color: '#8B6914', textDecoration: 'none', letterSpacing: '0.04em' }}
                  onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                  onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                >
                  x community →
                </a>
              </div>

              {/* Telegram */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0' }}>
                <span className="font-mono" style={{ fontSize: 10, color: 'rgba(28,26,22,0.4)', letterSpacing: '0.12em', width: 80, flexShrink: 0 }}>telegram</span>
                <a
                  href="https://t.me/fablectofficial"
                  target="_blank" rel="noopener noreferrer"
                  className="font-mono"
                  style={{ fontSize: 11, color: '#8B6914', textDecoration: 'none', letterSpacing: '0.04em' }}
                  onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                  onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                >
                  t.me/fablectofficial →
                </a>
              </div>
            </div>
          </Section>

        </main>

        {/* ── FOOTER ── */}
        <footer style={{
          borderTop: '1px solid rgba(28,26,22,0.1)',
          padding: '28px 24px',
          background: 'rgba(28,26,22,0.02)',
        }}>
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <div className="font-mono" style={{ fontSize: 10, color: 'rgba(28,26,22,0.35)', lineHeight: 2, letterSpacing: '0.06em' }}>
              <div>$fable · robinhood chain · community takeover (CTO)</div>
              <div style={{ marginTop: 4, color: 'rgba(28,26,22,0.25)', fontSize: 9 }}>
                speculative memecoin · no financial advice · no affiliation with any AI company or named individual
              </div>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}
